import {
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';

import { QaiButtonComponent, QaiDialogComponent } from '@qai/ui';

import { AvatarLookVoiceSelectorComponent, AvatarSelection } from 'src/app/pages/self-serve/simulation-builder/components/avatar-look-voice-selector/avatar-look-voice-selector.component';
import { SelfAuthoringPersonaFormService } from 'src/app/pages/self-serve/simulation-builder/services/self-authoring-persona-form.service';
import { TestSimulationLauncherService } from 'src/app/services/self-serve/test-simulation-launcher.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';

@Component({
  selector: 'avatar-selection-modal',
  standalone: true,
  templateUrl: './avatar-selection-modal.component.html',
  styleUrls: ['./avatar-selection-modal.component.scss'],
  imports: [QaiButtonComponent, QaiDialogComponent, AvatarLookVoiceSelectorComponent, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AvatarSelectionModalComponent implements OnChanges {
  private readonly selfAuthoringPersonaFormService = inject(SelfAuthoringPersonaFormService);
  private readonly testSimulationLauncherService = inject(TestSimulationLauncherService);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = input(false);
  readonly title = input<string>('');
  readonly form = input<FormGroup>();
  readonly callUuid = input<string>('');
  readonly moduleUuid = input<string>('');

  readonly backToSimulationDetails = output<void>();
  readonly dismissed = output<void>();

  @ViewChild('dialog') dialogRef?: QaiDialogComponent;
  @ViewChild('avatarVoiceSelector') avatarVoiceSelector?: AvatarLookVoiceSelectorComponent;

  readonly isLaunchingTestSimulation = signal(false);
  readonly isSavingPersona = computed(() => this.selfAuthoringPersonaFormService.isSaving(this.callUuid())());

  private closedViaBackButton = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.open() ? this.dialogRef?.showModal() : this.dialogRef?.close();
    }
  }

  handleAvatarChange(selection: AvatarSelection | undefined): void {
    if (!this.callUuid()) return;
    this.selfAuthoringPersonaFormService.updateAvatarSelection(this.callUuid(), selection?.replicaId, selection?.gender);
  }

  handleBackClick(): void {
    this.closedViaBackButton = true;
    this.backToSimulationDetails.emit();
  }

  handleAfterClose(): void {
    if (this.closedViaBackButton) {
      this.closedViaBackButton = false;
      return;
    }
    this.avatarVoiceSelector?.resetSelection();
    this.dismissed.emit();
  }

  handleTestSimulationClick(): void {
    const callUuid = this.callUuid();
    const moduleUuid = this.moduleUuid();
    const currentUserUuid = SelfServeLocalStorageUtil.getCurrentUser().userUuid;
    if (
      !callUuid ||
      !moduleUuid ||
      !currentUserUuid ||
      !this.avatarVoiceSelector?.selectedAvatar() ||
      this.isSavingPersona() ||
      this.isLaunchingTestSimulation()
    ) {
      console.warn('Test simulation launch skipped: missing required context, already launching, or persona still saving', {
        callUuid,
        moduleUuid,
        currentUserUuid,
        hasSelectedAvatar: !!this.avatarVoiceSelector?.selectedAvatar(),
        isSavingPersona: this.isSavingPersona(),
        isLaunchingTestSimulation: this.isLaunchingTestSimulation(),
      });
      return;
    }

    this.isLaunchingTestSimulation.set(true);

    this.testSimulationLauncherService.launchTestSimulation(callUuid, moduleUuid, currentUserUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLaunchingTestSimulation.set(false);
        },
        error: (err) => {
          this.isLaunchingTestSimulation.set(false);
          console.error('Test simulation launch failed', err);
        },
      });
  }
}
